import { useState, useEffect, useRef } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { executiveSummaryAPI, organizationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    TrendingUp,
    TrendingDown,
    DollarSign,
    Star,
    Users,
    Calendar,
    Download,
    FileText,
    RefreshCw,
    Award,
    AlertTriangle,
    Building2,
    ChevronDown,
    ChevronUp,
    Loader2,
    HelpCircle,
    BarChart3,
    Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    Tooltip as RechartsTooltip
} from 'recharts';
import jsPDF from 'jspdf';
import { parseUtc } from '../lib/datetime';

// Platform colors for pie chart
const PLATFORM_COLORS = {
    google_reviews: '#FF00FF',
    facebook: '#1877F2',
    instagram: '#E4405F',
    yelp: '#D32323',
    trustpilot: '#00B67A',
    tripadvisor: '#34E0A1'
};

const PLATFORM_LABELS = {
    google_reviews: 'Google',
    facebook: 'Facebook',
    instagram: 'Instagram',
    yelp: 'Yelp',
    trustpilot: 'Trustpilot',
    tripadvisor: 'TripAdvisor'
};

// Help tooltip component
const HelpTooltip = ({ text }) => (
    <TooltipProvider>
        <Tooltip>
            <TooltipTrigger asChild>
                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help inline-block ml-1" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs z-[100]">
                <p className="text-sm">{text}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

// Format currency
const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

// Format number with commas
const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
};

export default function ExecutiveSummary() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [data, setData] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(
        // Arriving from the Command Center, which puts its chosen organization
        // in the URL (?org=…). Without this the drill-down silently widened
        // back to every organization.
        new URLSearchParams(window.location.search).get('org') || 'all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [showAllLocations, setShowAllLocations] = useState(false);
    
    const { user, isSuperAdmin, canExportFull } = useAuth();
    const reportRef = useRef(null);

    useEffect(() => {
        // Set default dates (last 30 days)
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        
        setEndDate(end.toISOString().split('T')[0]);
        setStartDate(start.toISOString().split('T')[0]);
        
        loadOrganizations();
    }, []);

    useEffect(() => {
        if (startDate && endDate) {
            loadData();
        }
    }, [startDate, endDate, selectedOrg]);

    const loadOrganizations = async () => {
        if (isSuperAdmin()) {
            try {
                const res = await organizationsAPI.list();
                setOrganizations(sortByName(res.data));
            } catch (error) {
                console.error('Failed to load organizations');
            }
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const params = {
                start_date: startDate,
                end_date: endDate
            };
            if (selectedOrg && selectedOrg !== 'all') {
                params.organization_id = selectedOrg;
            }
            
            const res = await executiveSummaryAPI.getSummary(params);
            setData(res.data);
        } catch (error) {
            toast.error('Failed to load executive summary');
        } finally {
            setLoading(false);
        }
    };

    const exportToPDF = async () => {
        if (!data) return;

        // Localized labels for the PDF export, with English fallback
        // so a missing translation never blocks the download. jsPDF
        // v4 rejects raw numbers passed to `doc.text`, so every
        // value is coerced through `s()` below.
        const T = (key, en) => t(`pdfExports.executive.${key}`, en);
        const s = (v, fallback = '—') => {
            if (v === null || v === undefined) return String(fallback);
            if (typeof v === 'number') return Number.isFinite(v) ? String(v) : String(fallback);
            return String(v);
        };

        setExporting(true);
        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const margin = 15;
            let y = 15;

            // Header
            pdf.setFontSize(24);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(30, 41, 59);
            pdf.text(s(T('title', 'YulOnTap Executive Summary')), margin, y);
            y += 10;

            // Date range
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(100, 116, 139);
            const dr = data.date_range || {};
            pdf.text(
                s(`${dr.start ?? ''} – ${dr.end ?? ''} (${dr.days ?? 0} ${T('days', 'days')})`),
                margin, y,
            );
            y += 15;

            // Hero Stats Box
            pdf.setFillColor(16, 185, 129);
            pdf.roundedRect(margin, y, pageWidth - margin * 2, 35, 3, 3, 'F');

            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'normal');

            const statsY = y + 10;
            const col1 = margin + 10;
            const col2 = margin + 55;
            const col3 = margin + 105;
            const col4 = margin + 150;

            pdf.text(s(T('cardTotalReviews', 'Total Reviews')), col1, statsY);
            pdf.text(s(T('cardTotalRoi', 'Total ROI')), col2, statsY);
            pdf.text(s(T('cardAvgDaily', 'Avg Daily')), col3, statsY);
            pdf.text(s(T('cardLocations', 'Locations')), col4, statsY);

            const hero = data.hero_stats || {};
            pdf.setFontSize(18);
            pdf.setFont('helvetica', 'bold');
            pdf.text(s(formatNumber(hero.total_incremental_reviews)), col1, statsY + 10);
            pdf.text(s(formatCurrency(hero.total_roi_value)), col2, statsY + 10);
            pdf.text(s(`${hero.avg_daily_velocity ?? 0}/${T('dayShort', 'day')}`), col3, statsY + 10);
            pdf.text(s(hero.total_locations, '0'), col4, statsY + 10);

            y += 45;

            // Top Performers
            pdf.setTextColor(30, 41, 59);
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text(s(T('sectionTopPerformers', 'Top 5 Performing Locations')), margin, y);
            y += 8;

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');

            const topLocs = Array.isArray(data.top_locations) ? data.top_locations : [];
            if (topLocs.length > 0) {
                topLocs.forEach((loc, i) => {
                    pdf.setTextColor(100, 116, 139);
                    pdf.text(s(`${i + 1}. ${loc.location_name ?? '—'}`), margin + 5, y);
                    pdf.setTextColor(16, 185, 129);
                    pdf.text(
                        s(`${formatNumber(loc.incremental_reviews)} ${T('reviewsShort', 'reviews')} | ${formatCurrency(loc.roi_value)}`),
                        margin + 80, y,
                    );
                    y += 6;
                });
            } else {
                pdf.setTextColor(100, 116, 139);
                pdf.text(s(T('empty', 'No data available')), margin + 5, y);
                y += 6;
            }

            y += 8;

            // Bottom Performers
            pdf.setTextColor(30, 41, 59);
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text(s(T('sectionBottomPerformers', 'Locations Needing Attention')), margin, y);
            y += 8;

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');

            const botLocs = Array.isArray(data.bottom_locations) ? data.bottom_locations : [];
            if (botLocs.length > 0) {
                botLocs.forEach((loc, i) => {
                    pdf.setTextColor(100, 116, 139);
                    pdf.text(s(`${i + 1}. ${loc.location_name ?? '—'}`), margin + 5, y);
                    pdf.setTextColor(239, 68, 68);
                    pdf.text(
                        s(`${formatNumber(loc.incremental_reviews)} ${T('reviewsShort', 'reviews')} | ${formatCurrency(loc.roi_value)}`),
                        margin + 80, y,
                    );
                    y += 6;
                });
            } else {
                pdf.setTextColor(100, 116, 139);
                pdf.text(s(T('allGood', 'All locations performing well!')), margin + 5, y);
                y += 6;
            }

            y += 10;

            // Platform Breakdown
            pdf.setTextColor(30, 41, 59);
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text(s(T('sectionPlatforms', 'Reviews by Platform')), margin, y);
            y += 8;

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');

            const platformMap = data.platform_breakdown || {};
            const totalPlatformReviews = Object.values(platformMap)
                .reduce((a, b) => a + (Number(b) || 0), 0);
            Object.entries(platformMap).forEach(([platform, count]) => {
                const n = Number(count) || 0;
                const pct = totalPlatformReviews > 0
                    ? ((n / totalPlatformReviews) * 100).toFixed(1)
                    : '0';
                pdf.setTextColor(100, 116, 139);
                pdf.text(
                    s(`${PLATFORM_LABELS[platform] || platform}: ${formatNumber(n)} (${pct}%)`),
                    margin + 5, y,
                );
                y += 5;
            });

            y += 10;

            // Staff Adoption
            pdf.setTextColor(30, 41, 59);
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text(s(T('sectionStaffAdoption', 'Staff Adoption')), margin, y);
            y += 8;

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(100, 116, 139);
            const sa = data.staff_adoption || {};
            pdf.text(
                s(`${sa.active_employees ?? 0} ${T('adoptionOf', 'of')} ${sa.total_employees ?? 0} ${T('adoptionActive', 'employees active')} (${sa.rate ?? 0}%)`),
                margin + 5, y,
            );
            y += 10;

            // Month-over-month
            pdf.setTextColor(30, 41, 59);
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text(s(T('sectionGrowth', 'Month-over-Month Growth')), margin, y);
            y += 8;

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            const mom = data.month_over_month || {};
            const chg = Number(mom.change_percent ?? 0);
            const momColor = chg >= 0 ? [16, 185, 129] : [239, 68, 68];
            pdf.setTextColor(...momColor);
            const momSign = chg >= 0 ? '+' : '';
            pdf.text(
                s(`${momSign}${chg}% (${mom.current ?? 0} ${T('growthVs', 'vs')} ${mom.previous ?? 0} ${T('growthLastMonth', 'last month')})`),
                margin + 5, y,
            );

            // Footer
            y = pdf.internal.pageSize.getHeight() - 15;
            pdf.setTextColor(156, 163, 175);
            pdf.setFontSize(8);
            pdf.text(
                s(`${T('footerGenerated', 'Generated by YulOnTap')} | ${new Date().toLocaleDateString()}`),
                margin, y,
            );
            pdf.text('portal.yulontap.com', pageWidth - margin - 30, y);

            // Save
            const fileName = `YulOnTap_Executive_Summary_${startDate}_to_${endDate}.pdf`;
            pdf.save(fileName);
            toast.success('PDF exported successfully');
        } catch (error) {
            // Detailed diagnostics survive the generic user-facing
            // toast — matches the SalesReport pattern.
            console.error('[PDF] executive summary export failed', {
                message: error?.message,
                name: error?.name,
                stack: error?.stack,
                hasData: !!data,
                heroKeys: data?.hero_stats ? Object.keys(data.hero_stats) : [],
                platformKeys: data?.platform_breakdown ? Object.keys(data.platform_breakdown) : [],
            });
            toast.error('Failed to export PDF');
        } finally {
            setExporting(false);
        }
    };

    const exportToCSV = () => {
        if (!data) return;
        
        const rows = [
            ['YulOnTap Executive Summary'],
            [`Date Range: ${data.date_range.start} to ${data.date_range.end}`],
            [],
            ['Hero Stats'],
            ['Total Reviews', data.hero_stats.total_incremental_reviews],
            ['Total ROI Value', `$${data.hero_stats.total_roi_value}`],
            ['Avg Daily Velocity', data.hero_stats.avg_daily_velocity],
            ['Total Locations', data.hero_stats.total_locations],
            [],
            ['Location Rankings'],
            ['Location', 'Reviews', 'ROI Value', 'Velocity'],
            ...data.all_locations.map(loc => [
                loc.location_name,
                loc.incremental_reviews,
                `$${loc.roi_value}`,
                loc.velocity
            ]),
            [],
            ['Platform Breakdown'],
            ...Object.entries(data.platform_breakdown || {}).map(([p, c]) => [PLATFORM_LABELS[p] || p, c]),
            [],
            ['Staff Adoption'],
            ['Active Employees', data.staff_adoption.active_employees],
            ['Total Employees', data.staff_adoption.total_employees],
            ['Adoption Rate', `${data.staff_adoption.rate}%`]
        ];
        
        const csvContent = rows.map(r => r.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `YulOnTap_Executive_Summary_${startDate}_to_${endDate}.csv`;
        link.click();
        toast.success('CSV exported successfully');
    };

    // Prepare pie chart data
    const pieData = data ? Object.entries(data.platform_breakdown || {}).map(([platform, count]) => ({
        name: PLATFORM_LABELS[platform] || platform,
        value: count,
        color: PLATFORM_COLORS[platform] || '#6B7280'
    })) : [];

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="executive-summary-page">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('executiveSummary.title')}
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('executiveSummary.titleHint')}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" onClick={loadData} disabled={loading}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        {t('executiveSummary.refresh')}
                    </Button>
                    {canExportFull() && (
                        <Button variant="outline" onClick={exportToCSV} disabled={!data}>
                            <FileText className="w-4 h-4 mr-2" />
                            {t('executiveSummary.exportCSV')}
                        </Button>
                    )}
                    <Button onClick={exportToPDF} disabled={!data || exporting} className="bg-emerald-600 hover:bg-emerald-700">
                        {exporting ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Download className="w-4 h-4 mr-2" />
                        )}
                        {t('executiveSummary.exportPDF')}
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-end gap-4">
                        {isSuperAdmin() && (
                            <div className="w-48">
                                <Label className="text-sm mb-1 block">{t('locationHealth.organization')}</Label>
                                <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('executiveSummary.allOrganizations')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('executiveSummary.allOrganizations')}</SelectItem>
                                        {organizations.map(org => (
                                            <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div>
                            <Label className="text-sm mb-1 block">{t('executiveSummary.startDate')}</Label>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-40"
                            />
                        </div>
                        <div>
                            <Label className="text-sm mb-1 block">{t('executiveSummary.endDate')}</Label>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-40"
                            />
                        </div>
                        {data && (
                            <Badge variant="outline" className="h-10 px-4">
                                {t('executiveSummary.daysSelected', { count: data.date_range?.days || 0 })}
                            </Badge>
                        )}
                    </div>
                </CardContent>
            </Card>

            {data && (
                <div ref={reportRef}>
                    {/* Hero Stats */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-3 mb-2">
                                    <Star className="w-5 h-5 opacity-80" />
                                    <span className="text-sm opacity-90">{t('executiveSummary.totalReviews')}</span>
                                </div>
                                <p className="text-3xl font-bold">{formatNumber(data.hero_stats.total_incremental_reviews)}</p>
                                <p className="text-sm opacity-75 mt-1">{t('executiveSummary.totalReviewsHint')}</p>
                            </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-3 mb-2">
                                    <DollarSign className="w-5 h-5 opacity-80" />
                                    <span className="text-sm opacity-90">{t('executiveSummary.totalROIValue')}</span>
                                </div>
                                <p className="text-3xl font-bold">{formatCurrency(data.hero_stats.total_roi_value)}</p>
                                <p className="text-sm opacity-75 mt-1">{t('executiveSummary.totalROIValueHint')}</p>
                            </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-3 mb-2">
                                    <Activity className="w-5 h-5 opacity-80" />
                                    <span className="text-sm opacity-90">{t('executiveSummary.avgDailyVelocity')}</span>
                                </div>
                                <p className="text-3xl font-bold">{data.hero_stats.avg_daily_velocity}</p>
                                <p className="text-sm opacity-75 mt-1">{t('executiveSummary.avgDailyVelocityHint')}</p>
                            </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-3 mb-2">
                                    <Building2 className="w-5 h-5 opacity-80" />
                                    <span className="text-sm opacity-90">{t('executiveSummary.activeLocations')}</span>
                                </div>
                                <p className="text-3xl font-bold">{data.hero_stats.total_locations}</p>
                                <p className="text-sm opacity-75 mt-1">{t('executiveSummary.activeLocationsHint')}</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Main Content Grid */}
                    <div className="grid lg:grid-cols-3 gap-6">
                        {/* Left Column - Rankings */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Top Performers */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <Award className="w-5 h-5 text-emerald-500" />
                                        {t('executiveSummary.topPerforming')}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {data.top_locations.length > 0 ? (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-10">#</TableHead>
                                                    <TableHead>{t('executiveSummary.location')}</TableHead>
                                                    <TableHead className="text-right">{t('executiveSummary.reviews')}</TableHead>
                                                    <TableHead className="text-right">{t('executiveSummary.roi')}</TableHead>
                                                    <TableHead className="text-right">{t('executiveSummary.velocity')}</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {data.top_locations.map((loc, i) => (
                                                    <TableRow key={loc.location_id}>
                                                        <TableCell>
                                                            <Badge className={i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 dark:bg-slate-700 text-muted-foreground'}>
                                                                {i + 1}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="font-medium">{loc.location_name}</TableCell>
                                                        <TableCell className="text-right text-emerald-600 font-semibold">
                                                            {formatNumber(loc.incremental_reviews)}
                                                        </TableCell>
                                                        <TableCell className="text-right text-emerald-600 font-semibold">
                                                            {formatCurrency(loc.roi_value)}
                                                        </TableCell>
                                                        <TableCell className="text-right text-muted-foreground">
                                                            {loc.velocity}/day
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        <p className="text-muted-foreground text-center py-6">{t('executiveSummary.noDataForPeriod')}</p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Bottom Performers */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                                        {t('executiveSummary.needingAttention')}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {data.bottom_locations.length > 0 ? (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-10">#</TableHead>
                                                    <TableHead>{t('executiveSummary.location')}</TableHead>
                                                    <TableHead className="text-right">{t('executiveSummary.reviews')}</TableHead>
                                                    <TableHead className="text-right">{t('executiveSummary.roi')}</TableHead>
                                                    <TableHead className="text-right">{t('executiveSummary.velocity')}</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {data.bottom_locations.map((loc, i) => (
                                                    <TableRow key={loc.location_id}>
                                                        <TableCell>
                                                            <Badge className="bg-red-100 text-red-700">{i + 1}</Badge>
                                                        </TableCell>
                                                        <TableCell className="font-medium">{loc.location_name}</TableCell>
                                                        <TableCell className="text-right text-red-600">
                                                            {formatNumber(loc.incremental_reviews)}
                                                        </TableCell>
                                                        <TableCell className="text-right text-red-600">
                                                            {formatCurrency(loc.roi_value)}
                                                        </TableCell>
                                                        <TableCell className="text-right text-muted-foreground">
                                                            {loc.velocity}/day
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        <p className="text-emerald-600 text-center py-6 font-medium">
                                            🎉 All locations performing well!
                                        </p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Velocity Trend Chart */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <BarChart3 className="w-5 h-5 text-blue-500" />
                                        Review Velocity Trend (Last 14 Days)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-64">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={data.velocity_trend}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                                <XAxis 
                                                    dataKey="date" 
                                                    tick={{ fontSize: 12 }}
                                                    tickFormatter={(val) => parseUtc(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                />
                                                <YAxis tick={{ fontSize: 12 }} />
                                                <RechartsTooltip 
                                                    formatter={(value) => [value, 'Reviews']}
                                                    labelFormatter={(label) => parseUtc(label).toLocaleDateString()}
                                                />
                                                <Line 
                                                    type="monotone" 
                                                    dataKey="reviews" 
                                                    stroke="#10B981" 
                                                    strokeWidth={2}
                                                    dot={{ fill: '#10B981' }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Right Column - Stats */}
                        <div className="space-y-6">
                            {/* Platform Breakdown */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-lg">{t('executiveSummary.reviewsByPlatform')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {pieData.length > 0 ? (
                                        <>
                                            <div className="h-48 mb-4">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={pieData}
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius={40}
                                                            outerRadius={70}
                                                            dataKey="value"
                                                        >
                                                            {pieData.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                                            ))}
                                                        </Pie>
                                                        <RechartsTooltip />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                            <div className="space-y-2">
                                                {pieData.map((item) => (
                                                    <div key={item.name} className="flex items-center justify-between text-sm">
                                                        <div className="flex items-center gap-2">
                                                            <div 
                                                                className="w-3 h-3 rounded-full" 
                                                                style={{ backgroundColor: item.color }}
                                                            />
                                                            <span>{item.name}</span>
                                                        </div>
                                                        <span className="font-medium">{formatNumber(item.value)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <p className="text-muted-foreground text-center py-6">{t('executiveSummary.noPlatformData')}</p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Staff Adoption */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <Users className="w-5 h-5 text-purple-500" />
                                        {t('locationHealth.staffAdoption')}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-center mb-4">
                                        <p className="text-4xl font-bold text-purple-600">{data.staff_adoption.rate}%</p>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            {t('executiveSummary.activeEmployees', { active: data.staff_adoption.active_employees, total: data.staff_adoption.total_employees })}
                                        </p>
                                    </div>
                                    <div className="h-2 bg-slate-200 dark:bg-slate-600 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-purple-500 rounded-full transition-all"
                                            style={{ width: `${Math.min(100, data.staff_adoption.rate)}%` }}
                                        />
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Month-over-Month */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        {data.month_over_month.change_percent >= 0 ? (
                                            <TrendingUp className="w-5 h-5 text-emerald-500" />
                                        ) : (
                                            <TrendingDown className="w-5 h-5 text-red-500" />
                                        )}
                                        {t('executiveSummary.monthOverMonth')}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-center">
                                        <p className={`text-4xl font-bold ${data.month_over_month.change_percent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {data.month_over_month.change_percent >= 0 ? '+' : ''}{data.month_over_month.change_percent}%
                                        </p>
                                        <p className="text-sm text-muted-foreground mt-2">
                                            <span className="font-medium">{formatNumber(data.month_over_month.current)}</span> this month vs{' '}
                                            <span className="font-medium">{formatNumber(data.month_over_month.previous)}</span> last month
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Platform Values Used */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-lg">
                                        {t('executiveSummary.roiValuesUsed')}
                                        <HelpTooltip text={t('executiveSummary.roiValuesHint', { defaultValue: 'These are the dollar values assigned to each platform for ROI calculations' })} />
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">{t('executiveSummary.googleReview')}</span>
                                            <span className="font-medium">${data.platform_values_used.google_reviews}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Facebook</span>
                                            <span className="font-medium">${data.platform_values_used.facebook}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Instagram</span>
                                            <span className="font-medium">${data.platform_values_used.instagram}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Yelp</span>
                                            <span className="font-medium">${data.platform_values_used.yelp}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Trustpilot</span>
                                            <span className="font-medium">${data.platform_values_used.trustpilot}</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* All Locations (Collapsible) */}
                    <Card className="mt-6">
                        <CardHeader 
                            className="cursor-pointer" 
                            onClick={() => setShowAllLocations(!showAllLocations)}
                        >
                            <CardTitle className="flex items-center justify-between">
                                <span className="flex items-center gap-2">
                                    <Building2 className="w-5 h-5 text-muted-foreground" />
                                    All Locations ({data.all_locations.length})
                                </span>
                                {showAllLocations ? (
                                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                                ) : (
                                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                                )}
                            </CardTitle>
                        </CardHeader>
                        {showAllLocations && (
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Rank</TableHead>
                                            <TableHead>Location</TableHead>
                                            <TableHead className="text-right">Reviews</TableHead>
                                            <TableHead className="text-right">ROI Value</TableHead>
                                            <TableHead className="text-right">Velocity</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {data.all_locations.map((loc, i) => (
                                            <TableRow key={loc.location_id}>
                                                <TableCell>
                                                    <Badge variant="outline">{i + 1}</Badge>
                                                </TableCell>
                                                <TableCell className="font-medium">{loc.location_name}</TableCell>
                                                <TableCell className="text-right">{formatNumber(loc.incremental_reviews)}</TableCell>
                                                <TableCell className="text-right">{formatCurrency(loc.roi_value)}</TableCell>
                                                <TableCell className="text-right">{loc.velocity}/day</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        )}
                    </Card>
                </div>
            )}
        </div>
    );
}
